
import { IReturn } from "@servicestack/client";
import { useMemo } from "react";
import { KeyedMutator, SWRConfiguration } from "swr";
import { IApiConfiguration, IOnlinePatientManagement } from "@ngt/opms";
import useSwrClient from "./useSwrClient";
import IHttpVerb from "../../types/IHttpVerb";

export const useApi = <T, D, E, A>(httpVerb: IHttpVerb, fn?: (opms: IOnlinePatientManagement) => IReturn<T>, getConfiguration?: IApiConfiguration<T, D, E, A>, swrConfiguration?: SWRConfiguration | undefined) => {
    const { api } = useSwrClient();
    const response = api<T>(httpVerb, fn, swrConfiguration);

    const { getData, getError, getAdditionalState } = getConfiguration ?? {};

    const result: {
        data: D | undefined,
        error: E | undefined,
        loading: boolean,
        validating: boolean,
        mutate: KeyedMutator<T>;
        refresh: () => Promise<D | undefined>;
    } & A = useMemo(() => {
        const d = getData?.(response);
        const e = getError?.(response);
        const a = getAdditionalState?.(response);

        const s = {
            data: d,
            error: e,
            loading: response.isValidating && !d && !e,
            validating: response.isValidating,
            mutate: response.mutate,
            refresh: async () => {
                const newResponse = await response.mutate(response.data, { revalidate: true });

                return getData?.(newResponse as any);
            }
        }

        return {
            ...s,
            ...a
        } as typeof s & A

    }, [getData, getError, response.data, response.error, response.isLoading, response.isValidating, response.mutate])

    return result;
}

export default useApi;